import { isDeadEnd } from './prospects';
import type { Prospect } from './prospects';
import { reachedStage } from './prospectFilters';
import type { ProspectFilter } from './prospectFilters';

/**
 * The prospect funnel (drop A, 17 September): the Pipeline tab's Outcome
 * panel, for prospects. Kyu's stages, in his words: New › Requested ›
 * Connected › 1st DM › 2nd DM › 3rd DM › Promoted to lead, with Left company
 * and Can't connect as the two ways out.
 *
 * Every stage counts REACHED OR PASSED, worked out from the one stored
 * status through PROSPECT_STAGE_RANK. That is what makes it a funnel: a
 * later stage can never read larger than an earlier one, and a prospect who
 * jumped from Request sent straight to a lead counts in Arrived, Requested
 * and Promoted, not in the DMs nobody sent. A dead end counts in Arrived and
 * in its own row and nowhere else, because the single value cannot say how
 * far they got before they left, and guessing would be inventing history.
 *
 * Each row carries the filter that shows exactly the people its number
 * counts, so the number and the list underneath it never disagree.
 */

export type FunnelStageKey =
  'arrived' | 'requested' | 'connected' | 'dm_1' | 'dm_2' | 'dm_3' | 'promoted';

export type DeadEndKey = 'left_company' | 'cant_connect';

export interface FunnelLine<K extends string> {
  key: K;
  label: string;
  count: number;
  /** Of Arrived. 0 when nobody arrived. */
  percent: number;
  filter: ProspectFilter;
}

export const FUNNEL_EXPLAINERS: Record<FunnelStageKey | DeadEndKey, string> = {
  arrived: 'Everyone in the list you are looking at.',
  requested: 'A connection request went out, or anything after that.',
  connected: 'They accepted, or anything after that.',
  dm_1: 'The first message went out, or anything after that.',
  dm_2: 'A second message went out, or anything after that.',
  dm_3: 'A third message went out.',
  promoted: 'Became a lead. Their work continues on the Leads tab.',
  left_company: 'Marked as having left the company. Out of the funnel.',
  cant_connect: 'Marked as unreachable on LinkedIn. Out of the funnel.',
};

const STAGES: { key: FunnelStageKey; label: string; filter: ProspectFilter }[] = [
  { key: 'arrived', label: 'Arrived', filter: 'all' },
  { key: 'requested', label: 'Requested', filter: 'reached_pending' },
  { key: 'connected', label: 'Connected', filter: 'reached_connected' },
  { key: 'dm_1', label: '1st DM', filter: 'reached_pitched' },
  { key: 'dm_2', label: '2nd DM', filter: 'reached_dm_2' },
  { key: 'dm_3', label: '3rd DM', filter: 'reached_dm_3' },
  { key: 'promoted', label: 'Promoted', filter: 'promoted' },
];

const DEAD_ENDS: { key: DeadEndKey; label: string; filter: ProspectFilter }[] = [
  { key: 'left_company', label: 'Left company', filter: 'left_company' },
  { key: 'cant_connect', label: "Can't connect", filter: 'cant_connect' },
];

function percentOf(count: number, total: number): number {
  if (total === 0) return 0;
  const pct = (count / total) * 100;
  // One decimal below 10, whole above: 0.2% is worth saying, 47.3% is not.
  return pct < 10 ? Math.round(pct * 10) / 10 : Math.round(pct);
}

function countStage(prospects: readonly Prospect[], key: FunnelStageKey): number {
  switch (key) {
    case 'arrived':
      return prospects.length;
    case 'promoted':
      return prospects.filter((p) => p.promoted_at !== null).length;
    case 'requested':
      return prospects.filter((p) => reachedStage(p, 'pending')).length;
    case 'connected':
      return prospects.filter((p) => reachedStage(p, 'connected')).length;
    case 'dm_1':
      return prospects.filter((p) => reachedStage(p, 'pitched')).length;
    case 'dm_2':
      return prospects.filter((p) => reachedStage(p, 'dm_2')).length;
    case 'dm_3':
      return prospects.filter((p) => reachedStage(p, 'dm_3')).length;
  }
}

export function buildProspectFunnel(prospects: readonly Prospect[]): {
  stages: FunnelLine<FunnelStageKey>[];
  deadEnds: FunnelLine<DeadEndKey>[];
} {
  const arrived = prospects.length;
  const stages = STAGES.map((stage) => {
    const count = countStage(prospects, stage.key);
    return { ...stage, count, percent: percentOf(count, arrived) };
  });
  const deadEnds = DEAD_ENDS.map((end) => {
    const count = prospects.filter(
      (p) => p.promoted_at === null && isDeadEnd(p.status) && p.status === end.key,
    ).length;
    return { ...end, count, percent: percentOf(count, arrived) };
  });
  return { stages, deadEnds };
}
